import Echo from 'laravel-echo';
import io from 'socket.io-client';
import { WEBSOCKET, BROADCAST_PREFIX } from './headers';

window.io = io;

/**
 * Conexión de tiempo real con laravel-echo-server.
 *
 * Se crea de forma perezosa (solo cuando alguna vista se suscribe a un canal)
 * y el token de sesión se lee en el momento de conectar, no al importar el
 * módulo: antes, quien iniciaba sesión sin recargar la página quedaba con un
 * socket autenticado con credenciales viejas o nulas.
 */

let instance = null;
let instanceToken = null;

const listeners = new Set();
let status = 'disconnected';

const currentToken = () => {
    try {
        return JSON.parse(localStorage.getItem('user'))?.token ?? null;
    } catch (error) {
        return null;
    }
};

const setStatus = (next) => {
    if (status === next) return;
    status = next;
    listeners.forEach((listener) => listener(status));
};

/** Nombre real del canal en Redis, incluido el prefijo de Laravel. */
export const channelName = (name) => `${BROADCAST_PREFIX}${name}`;

export const getConnectionStatus = () => status;

export const onConnectionStatus = (listener) => {
    listeners.add(listener);
    listener(status);
    return () => listeners.delete(listener);
};

const build = (token) => {
    const echo = new Echo({
        broadcaster: 'socket.io',
        host: WEBSOCKET,
        transports: ['websocket', 'polling'],
        // Reconexión explícita: por defecto socket.io reintenta de forma
        // indefinida y silenciosa, sin que la UI pueda avisar al usuario.
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        auth: {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                Accept: 'application/json',
            },
        },
    });

    const socket = echo.connector.socket;

    if (socket) {
        setStatus(socket.connected ? 'connected' : 'connecting');
        socket.on('connect', () => setStatus('connected'));
        socket.on('reconnect_attempt', () => setStatus('connecting'));
        socket.on('disconnect', () => setStatus('disconnected'));
        socket.on('connect_error', () => setStatus('error'));
        socket.on('error', () => setStatus('error'));
    }

    return echo;
};

/**
 * Instancia activa. Si el token de sesión cambió, se descarta la conexión
 * anterior y se abre una nueva con las credenciales correctas.
 */
export const getEcho = () => {
    const token = currentToken();

    if (instance && instanceToken !== token) {
        disconnectEcho();
    }

    if (!instance) {
        instanceToken = token;
        instance = build(token);
    }

    return instance;
};

export const disconnectEcho = () => {
    if (!instance) return;
    try {
        instance.disconnect();
    } catch (error) {
        // Cerrar una conexión ya caída no debe romper la navegación.
    }
    instance = null;
    instanceToken = null;
    setStatus('disconnected');
};

export default getEcho;
